import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Play,
  Trash2,
  Lock,
  X,
  Loader2,
  UserPlus,
  CheckCircle,
  Flag,
  Gavel,
  Ban,
  CalendarClock,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { api } from '../services/api';
import jokerHatIcon from '../assets/joker-hat.svg';
import { rankingCache } from './RankingDetailPage';
import { RankingRoundShareButton } from '../components/rankings/RankingRoundShareButton';
import { shortName, compactName } from '../utils/playerName';
import { WhatsAppMessageActions } from '../components/common/WhatsAppMessageActions';
import { trackEvent } from '../utils/analytics';
// localStorage-backed cache for PWA offline support
import { readPayloadCache as readLS, writePayloadCache as writeLS } from '../utils/payloadCache';
import type {
  RankingRoundDetail,
  RankingMatchDetail,
  RankingPlayer,
  WildcardCandidate,
  WildcardPriority,
} from '../types';

/**
 * The organizer's "who should play as coringa" list for the ranking's WhatsApp
 * group: players grouped by how many rounds they are behind, most behind
 * first. `*` is WhatsApp bold.
 */
function buildWildcardPriorityMessage(priority: WildcardPriority): string {
  const byBehind = new Map<number, string[]>();
  for (const p of priority.players) {
    const names = byBehind.get(p.roundsBehind) ?? [];
    names.push(p.name || 'Jogador');
    byBehind.set(p.roundsBehind, names);
  }
  const sections = [...byBehind.entries()]
    .sort(([a], [b]) => b - a)
    .map(([behind, names]) => {
      const sorted = [...names].sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
      const heading = `— ${behind} ${behind === 1 ? 'rodada' : 'rodadas'} atrás —`;
      return [heading, ...sorted.map((n) => `- ${n}`)].join('\n');
    });
  return [`🃏 *Prioridades de coringa - Rodada ${priority.roundNumber}*`, ...sections].join('\n\n');
}

export function RankingRoundPage() {
  const navigate = useNavigate();
  const { rankingId, roundId } = useParams<{ rankingId: string; roundId: string }>();
  const lsCacheKey = rankingId && roundId ? `bt_ranking_round_${rankingId}_${roundId}` : undefined;
  const cached = lsCacheKey ? readLS<RankingRoundDetail>(lsCacheKey) : null;
  const [round, setRound] = useState<RankingRoundDetail | null>(cached);
  const [isLoading, setIsLoading] = useState(!cached);
  const [didRefresh, setDidRefresh] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Modals
  const [showSubmitModal, setShowSubmitModal] = useState<RankingMatchDetail | null>(null);
  const [showOverrideModal, setShowOverrideModal] = useState<RankingMatchDetail | null>(null);
  const [showWoModal, setShowWoModal] = useState<RankingMatchDetail | null>(null);
  const [showWildcardModal, setShowWildcardModal] = useState<{ groupId: string; slotPosition: 2 | 3 | 4 } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showMarkPlayedConfirm, setShowMarkPlayedConfirm] = useState<{ groupId: string; groupNumber: number; value: boolean } | null>(null);

  // Ranking detail for admin check (seed from cache if available)
  const cachedRanking = rankingId ? rankingCache.get(rankingId) : null;
  const [isAdmin, setIsAdmin] = useState(cachedRanking?.myRole === 'admin');
  const [myPlayerId, setMyPlayerId] = useState<string | null>(cachedRanking?.myPlayerId ?? null);
  const [rankingName, setRankingName] = useState<string | null>(cachedRanking?.name ?? null);
  const [rankingImageUrl, setRankingImageUrl] = useState<string | null>(cachedRanking?.imageUrl ?? null);
  const [allowWildcardCatchUp, setAllowWildcardCatchUp] = useState<boolean>(
    cachedRanking?.scoringRules.allowWildcardCatchUp ?? true
  );
  const [showSwapModal, setShowSwapModal] = useState<{ groupId: string; slotPosition: 1 | 2 | 3 | 4; currentPlayerId: string } | null>(null);

  const loadRound = useCallback(async () => {
    if (!rankingId || !roundId) return;
    const hadCached = !!lsCacheKey && !!readLS(lsCacheKey);
    try {
      const [roundRes, rankingRes] = await Promise.all([
        api.rankings.roundDetail(rankingId, roundId),
        api.rankings.detail(rankingId),
      ]);
      if (roundRes.success && roundRes.data) {
        if (lsCacheKey) writeLS(lsCacheKey, roundRes.data);
        setRound(roundRes.data);
        if (hadCached) {
          setDidRefresh(true);
          setTimeout(() => setDidRefresh(false), 600);
        }
      }
      const gone =
        rankingRes.error?.code === 'NOT_FOUND' ||
        rankingRes.error?.code === 'NOT_MEMBER' ||
        roundRes.error?.code === 'NOT_FOUND';
      if (gone) {
        // Ranking deleted (or membership revoked): purge the cached copies so
        // this URL stops rendering a ranking that no longer exists.
        rankingCache.clear(rankingId);
        if (lsCacheKey) { try { localStorage.removeItem(lsCacheKey); } catch { /* unavailable */ } }
        setRound(null);
        navigate('/rankings', { replace: true });
        return;
      }
      if (rankingRes.success && rankingRes.data) {
        setIsAdmin(rankingRes.data.myRole === 'admin');
        setMyPlayerId(rankingRes.data.myPlayerId);
        setRankingName(rankingRes.data.name);
        setRankingImageUrl(rankingRes.data.imageUrl);
        setAllowWildcardCatchUp(rankingRes.data.scoringRules.allowWildcardCatchUp);
        rankingCache.set(rankingId, rankingRes.data);
      }
    } catch (error) {
      console.error('Failed to load round:', error);
    }
  }, [rankingId, roundId, lsCacheKey, navigate]);

  useEffect(() => {
    if (!rankingId || !roundId) return;
    if (cached) {
      loadRound().then(() => setIsLoading(false));
    } else {
      setIsLoading(true);
      loadRound().then(() => setIsLoading(false));
    }
  }, [rankingId, roundId]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Prioridades de coringa" (admins, while seats can still change). Prefetched
  // so share/copy run synchronously inside the tap (iOS), and keyed on the
  // seats only: a seat change refetches, a score submission or the
  // cached-then-fresh reload does not.
  const priorityKey = useMemo(() => {
    if (!isAdmin || !allowWildcardCatchUp || !round) return null;
    if (round.status !== 'drafted' && round.status !== 'in_progress') return null;
    return [
      round.id,
      ...round.groups.map(
        (g) =>
          `${g.id}:${g.players.map((p) => p?.id ?? '-').join(',')}:${g.wildcardPlayerIds.join(',')}:${g.countsAsPlayed ? 1 : 0}`
      ),
    ].join('|');
  }, [isAdmin, allowWildcardCatchUp, round]);
  const [priorityResult, setPriorityResult] = useState<{ key: string; data: WildcardPriority | 'error' } | null>(null);

  useEffect(() => {
    if (!priorityKey || !rankingId || !roundId) return;
    let cancelled = false;
    api.rankings
      .wildcardPriority(rankingId, roundId)
      .then((res) => {
        if (cancelled) return;
        setPriorityResult({ key: priorityKey, data: res.success && res.data ? res.data : 'error' });
      })
      .catch(() => {
        if (!cancelled) setPriorityResult({ key: priorityKey, data: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [priorityKey, rankingId, roundId]);

  // null = loading (nothing fetched yet for the current seats)
  const wildcardPriority = priorityKey && priorityResult?.key === priorityKey ? priorityResult.data : null;
  const wildcardPriorityMessage = useMemo(
    () =>
      wildcardPriority && wildcardPriority !== 'error' && wildcardPriority.players.length > 0
        ? buildWildcardPriorityMessage(wildcardPriority)
        : null,
    [wildcardPriority]
  );

  const handleStartRound = async () => {
    if (!rankingId || !roundId) return;
    setActionLoading('start');
    try {
      const response = await api.rankings.startRound(rankingId, roundId);
      // A failure here is almost always a 409: the auto-draft (or another admin)
      // started the round first. Reload either way so the page shows the truth.
      if (!response.success) console.warn('Start round refused:', response.error?.message);
      await loadRound();
    } catch (error) {
      console.error('Failed to start round:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteRound = async () => {
    if (!rankingId || !roundId) return;
    setActionLoading('delete');
    try {
      const response = await api.rankings.deleteRound(rankingId, roundId);
      if (response.success) navigate(`/rankings/${rankingId}`);
    } catch (error) {
      console.error('Failed to delete round:', error);
    } finally {
      setActionLoading(null);
      setShowDeleteConfirm(false);
    }
  };

  const handleCompleteRound = async () => {
    if (!rankingId || !roundId) return;
    setActionLoading('complete');
    try {
      const response = await api.rankings.completeRound(rankingId, roundId);
      if (response.success) await loadRound();
    } catch (error) {
      console.error('Failed to complete round:', error);
    } finally {
      setActionLoading(null);
      setShowCompleteConfirm(false);
    }
  };

  const handleSubmitMatch = async (matchId: string, team1Games: number, team2Games: number, tiebreakScore?: number | null) => {
    if (!rankingId) return;
    setActionLoading(`submit-${matchId}`);
    try {
      const response = await api.rankings.submitMatch(rankingId, matchId, { team1Games, team2Games, tiebreakScore });
      if (response.success) {
        await loadRound();
        setShowSubmitModal(null);
      }
    } catch (error) {
      console.error('Failed to submit match:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisputeMatch = async (matchId: string) => {
    if (!rankingId) return;
    setActionLoading(`dispute-${matchId}`);
    try {
      const response = await api.rankings.disputeMatch(rankingId, matchId);
      if (response.success) await loadRound();
    } catch (error) {
      console.error('Failed to dispute match:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleOverrideMatch = async (matchId: string, team1Games: number, team2Games: number, tiebreakScore?: number | null) => {
    if (!rankingId) return;
    setActionLoading(`override-${matchId}`);
    try {
      const response = await api.rankings.overrideMatch(rankingId, matchId, { team1Games, team2Games, tiebreakScore });
      if (response.success) {
        await loadRound();
        setShowOverrideModal(null);
      }
    } catch (error) {
      console.error('Failed to override match:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleWoMatch = async (matchId: string, absentPlayerIds: string[], applyPenalty: boolean) => {
    if (!rankingId) return;
    setActionLoading(`wo-${matchId}`);
    try {
      const response = await api.rankings.woMatch(rankingId, matchId, { absentPlayerIds, applyPenalty });
      if (response.success) {
        await loadRound();
        setShowWoModal(null);
      }
    } catch (error) {
      console.error('Failed to mark WO:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAssignWildcard = async (groupId: string, slotPosition: 2 | 3 | 4, playerId: string) => {
    if (!rankingId || !roundId) return;
    setActionLoading(`wildcard-${playerId}`);
    try {
      const response = await api.rankings.assignWildcard(rankingId, roundId, groupId, { playerId, slotPosition });
      if (response.success) {
        await loadRound();
        setShowWildcardModal(null);
      }
    } catch (error) {
      console.error('Failed to assign wildcard:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSwapPlayer = async (groupId: string, slotPosition: 1 | 2 | 3 | 4, newPlayerId: string) => {
    if (!rankingId || !roundId) return;
    setActionLoading(`swap-${newPlayerId}`);
    try {
      const response = await api.rankings.swapPlayer(rankingId, roundId, groupId, { slotPosition, newPlayerId });
      if (response.success) {
        await loadRound();
        setShowSwapModal(null);
      }
    } catch (error) {
      console.error('Failed to swap player:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkGroupPlayed = async (groupId: string, value: boolean) => {
    if (!rankingId || !roundId) return;
    setActionLoading(`mark-played-${groupId}`);
    try {
      const response = await api.rankings.markGroupPlayed(rankingId, roundId, groupId, { countsAsPlayed: value });
      if (response.success) await loadRound();
    } catch (error) {
      console.error('Failed to mark group as played:', error);
    } finally {
      setActionLoading(null);
      setShowMarkPlayedConfirm(null);
    }
  };

  if (isLoading) {
    return (
      <AppShell>
        <div className="space-y-5 max-w-lg mx-auto">
          <div className="h-4 w-16 bg-white/10 rounded animate-pulse" />
          <div className="h-6 w-48 bg-white/10 rounded animate-pulse" />
          {[1, 2].map(i => (
            <div key={i} className="glass-card rounded-2xl p-5 animate-pulse">
              <div className="h-5 w-32 bg-white/10 rounded mb-3" />
              <div className="space-y-2">
                <div className="h-16 bg-white/10 rounded-xl" />
                <div className="h-16 bg-white/10 rounded-xl" />
              </div>
            </div>
          ))}
        </div>
      </AppShell>
    );
  }

  if (!round) {
    return (
      <AppShell>
        <div className="text-center py-20">
          <p className="text-white/50">Rodada não encontrada</p>
          <Button onClick={() => navigate(`/rankings/${rankingId}`)} variant="secondary" className="mt-4">
            Voltar para ranking
          </Button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className={`space-y-5 max-w-lg mx-auto rounded-xl ${didRefresh ? 'animate-data-refresh' : ''}`}>
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <button
            onClick={() => navigate(`/rankings/${rankingId}`)}
            className="flex items-center gap-1.5 text-white/50 hover:text-white text-sm mb-3 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Ranking
          </button>

          <div className="flex items-center gap-3">
            <h1 className="font-display text-xl font-bold text-white">
              Rodada {round.roundNumber}
            </h1>
            <RoundStatusBadge status={round.status} />
          </div>
          {(round.autoDrafted || round.autoStarted) && (
            <p className="mt-1 text-xs text-white/40 flex items-center gap-1.5">
              <CalendarClock className="w-3.5 h-3.5" />
              {round.autoDrafted && round.autoStarted
                ? 'Sorteada e iniciada automaticamente'
                : round.autoDrafted
                  ? 'Sorteada automaticamente'
                  : 'Iniciada automaticamente'}
            </p>
          )}
        </motion.div>

        {/* Admin controls for drafted rounds */}
        {isAdmin && round.status === 'drafted' && (
          <Card delay={0.05}>
            <div className="space-y-2">
              <Button
                onClick={handleStartRound}
                fullWidth
                isLoading={actionLoading === 'start'}
              >
                <Play className="w-4 h-4" />
                Iniciar rodada
              </Button>
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                variant="ghost"
                fullWidth
                className="text-coral hover:bg-coral/10"
              >
                <Trash2 className="w-4 h-4" />
                Excluir rodada
              </Button>
            </div>
          </Card>
        )}

        {/* Who scores playing as coringa this round (admins only) */}
        {priorityKey && wildcardPriority !== 'error' && (
          <Card delay={0.08}>
            <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
              <img src={jokerHatIcon} alt="" className="w-4 h-4" />
              Prioridades de coringa
            </h3>
            {wildcardPriority === null ? (
              <div className="flex justify-center py-2">
                <Loader2 className="w-4 h-4 animate-spin text-white/40" />
              </div>
            ) : wildcardPriority.players.length === 0 || !wildcardPriorityMessage ? (
              <p className="text-sm text-white/40">Ninguém está com rodadas atrasadas nesta rodada.</p>
            ) : (
              <>
                <p className="text-sm text-white/60 mb-3">
                  {wildcardPriority.players.length === 1
                    ? '1 jogador pontua jogando de coringa'
                    : `${wildcardPriority.players.length} jogadores pontuam jogando de coringa`}
                </p>
                <WhatsAppMessageActions
                  message={wildcardPriorityMessage}
                  label="Enviar no WhatsApp"
                  onShare={() =>
                    trackEvent('btv_ranking_wildcard_priority_share', {
                      method: 'whatsapp',
                      player_count: wildcardPriority.players.length,
                      round_number: wildcardPriority.roundNumber,
                    })
                  }
                  onCopy={() =>
                    trackEvent('btv_ranking_wildcard_priority_share', {
                      method: 'copy',
                      player_count: wildcardPriority.players.length,
                      round_number: wildcardPriority.roundNumber,
                    })
                  }
                />
              </>
            )}
          </Card>
        )}

        {/* Groups - user's group first, then by group number */}
        {[...round.groups].sort((a, b) => {
          const aIsMyGroup = myPlayerId ? a.players.some(p => p?.id === myPlayerId) : false;
          const bIsMyGroup = myPlayerId ? b.players.some(p => p?.id === myPlayerId) : false;
          if (aIsMyGroup && !bIsMyGroup) return -1;
          if (!aIsMyGroup && bIsMyGroup) return 1;
          return a.groupNumber - b.groupNumber;
        }).map((group, gIndex) => {
          const isMyGroup = myPlayerId ? group.players.some(p => p?.id === myPlayerId) : false;
          return (
          <motion.div
            key={group.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + gIndex * 0.05 }}
          >
            <Card className={`!p-4 ${isMyGroup ? 'ring-1 ring-turquoise/40' : ''}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-white/70">
                  Grupo {group.groupNumber}
                  {isMyGroup && (
                    <span className="ml-2 text-[10px] font-medium text-turquoise bg-turquoise/10 px-1.5 py-0.5 rounded-full">
                      Seu grupo
                    </span>
                  )}
                  {group.countsAsPlayed && (
                    <span className="ml-2 text-[10px] font-medium text-white/40 bg-white/10 px-1.5 py-0.5 rounded-full">
                      Jogou sem pontuar
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <button
                      onClick={() => setShowMarkPlayedConfirm({ groupId: group.id, groupNumber: group.groupNumber, value: !group.countsAsPlayed })}
                      disabled={actionLoading === `mark-played-${group.id}`}
                      className="text-[10px] py-1 px-2 rounded-lg bg-white/5 text-white/50 hover:bg-white/10 transition-colors disabled:opacity-50"
                    >
                      {group.countsAsPlayed ? 'Desfazer' : 'Marcar jogado (s/ pontos)'}
                    </button>
                  )}
                  {(() => {
                    const prodYagoId = 'd4695391-d557-45d5-95e2-ad60b50044c8';
                    const stagYagoId = 'eee340f5-75fd-492b-8bda-11183ffc4175';
                    const isYago = myPlayerId === prodYagoId || myPlayerId === stagYagoId;
                    if (!isYago || !isMyGroup || !rankingId || !rankingName || !group.isComplete) return null;
                    const allScored = group.matches.length > 0 && group.matches.every(m => m.team1Games !== null && m.team2Games !== null);
                    if (!allScored) return null;
                    return (
                      <RankingRoundShareButton
                        rankingId={rankingId}
                        rankingName={rankingName}
                        imageUrl={rankingImageUrl}
                        roundNumber={round.roundNumber}
                        matches={group.matches}
                      />
                    );
                  })()}
                </div>
              </div>

              {/* Players */}
              <div className="flex flex-wrap gap-2 mb-4">
                {group.players.map((player, pIndex) => {
                  const isGroupMember = myPlayerId && group.players.some(p => p?.id === myPlayerId);
                  const canAssignWildcard = isAdmin || isGroupMember;
                  const canSwap = isAdmin && (round.status === 'drafted' || round.status === 'in_progress' || round.status === 'completed');

                  return (
                    <div key={pIndex}>
                      {player ? (
                        <button
                          onClick={() => canSwap ? setShowSwapModal({ groupId: group.id, slotPosition: (pIndex + 1) as 1 | 2 | 3 | 4, currentPlayerId: player.id }) : undefined}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm ${
                            group.wildcardPlayerIds.includes(player.id)
                              ? 'bg-sunny/10 text-sunny'
                              : 'bg-white/5 text-white/70'
                          } ${group.countsAsPlayed ? 'opacity-60' : ''} ${canSwap ? 'hover:bg-white/10 transition-colors cursor-pointer' : ''}`}
                        >
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-turquoise/30 to-coral/30 flex items-center justify-center text-[11px] font-bold text-white shrink-0">
                            {player.avatarUrl ? (
                              <img src={player.avatarUrl} alt="" className="w-6 h-6 rounded-full object-cover" />
                            ) : (
                              player.name?.charAt(0) || '?'
                            )}
                          </div>
                          <span className="truncate max-w-[100px]">{compactName(player.name) || 'Jogador'}</span>
                          {group.wildcardPlayerIds.includes(player.id) && (
                            <img src={jokerHatIcon} alt="Coringa" className="w-4 h-4 opacity-70" />
                          )}
                        </button>
                      ) : group.countsAsPlayed ? (
                        <span className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm border border-dashed border-white/10 text-white/20">
                          Vazio
                        </span>
                      ) : (
                        <button
                          onClick={() => canAssignWildcard ? setShowWildcardModal({ groupId: group.id, slotPosition: (pIndex + 1) as 2 | 3 | 4 }) : undefined}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm border border-dashed ${
                            canAssignWildcard
                              ? 'border-sunny/40 text-sunny hover:bg-sunny/10 transition-colors cursor-pointer'
                              : 'border-white/20 text-white/30 cursor-default'
                          }`}
                        >
                          <UserPlus className="w-4 h-4" />
                          Coringa
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Matches */}
              {group.isComplete ? (
                <div className="space-y-2">
                  {group.matches.map((match) => (
                    <MatchCard
                      key={match.id}
                      match={match}
                      isAdmin={isAdmin}
                      onSubmit={() => setShowSubmitModal(match)}
                      onDispute={() => handleDisputeMatch(match.id)}
                      onOverride={() => setShowOverrideModal(match)}
                      onWo={() => setShowWoModal(match)}
                      actionLoading={actionLoading}
                    />
                  ))}
                </div>
              ) : group.countsAsPlayed ? (
                <p className="text-xs text-white/30 text-center py-2">
                  Grupo marcado como jogado, sem pontuação
                </p>
              ) : (
                <p className="text-xs text-white/30 text-center py-2">
                  Atribua os coringas para gerar as partidas
                </p>
              )}
            </Card>
          </motion.div>
          );
        })}

        {/* Admin control to force-complete in-progress rounds */}
        {isAdmin && round.status === 'in_progress' && (
          <Card delay={0.05}>
            <Button
              onClick={() => setShowCompleteConfirm(true)}
              fullWidth
              isLoading={actionLoading === 'complete'}
            >
              <CheckCircle className="w-4 h-4" />
              Completar rodada
            </Button>
          </Card>
        )}
      </div>


      {/* Modals */}
      <SubmitMatchModal
        match={showSubmitModal}
        onClose={() => setShowSubmitModal(null)}
        onSubmit={handleSubmitMatch}
        isLoading={actionLoading?.startsWith('submit-') || false}
      />

      <SubmitMatchModal
        match={showOverrideModal}
        onClose={() => setShowOverrideModal(null)}
        onSubmit={(matchId, t1, t2, tiebreakScore) => handleOverrideMatch(matchId, t1, t2, tiebreakScore)}
        isLoading={actionLoading?.startsWith('override-') || false}
        title="Corrigir resultado"
      />

      <WoModal
        match={showWoModal}
        onClose={() => setShowWoModal(null)}
        onSubmit={handleWoMatch}
        isLoading={actionLoading?.startsWith('wo-') || false}
      />

      {showWildcardModal && rankingId && roundId && (
        <WildcardModal
          rankingId={rankingId}
          roundId={roundId}
          groupId={showWildcardModal.groupId}
          slotPosition={showWildcardModal.slotPosition}
          allowCatchUp={allowWildcardCatchUp}
          onClose={() => setShowWildcardModal(null)}
          onAssign={handleAssignWildcard}
          loadingPlayerId={actionLoading?.startsWith('wildcard-') ? actionLoading.replace('wildcard-', '') : null}
        />
      )}

      {showSwapModal && rankingId && roundId && (
        <SwapModal
          rankingId={rankingId}
          roundId={roundId}
          groupId={showSwapModal.groupId}
          slotPosition={showSwapModal.slotPosition}
          currentPlayerId={showSwapModal.currentPlayerId}
          allowCatchUp={allowWildcardCatchUp}
          onClose={() => setShowSwapModal(null)}
          onSwap={handleSwapPlayer}
          loadingPlayerId={actionLoading?.startsWith('swap-') ? actionLoading.replace('swap-', '') : null}
        />
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteRound}
        isLoading={actionLoading === 'delete'}
        title="Excluir rodada"
        message="Tem certeza? Todas as partidas desta rodada serão removidas."
        confirmLabel="Excluir"
      />

      <ConfirmModal
        isOpen={showCompleteConfirm}
        onClose={() => setShowCompleteConfirm(false)}
        onConfirm={handleCompleteRound}
        isLoading={actionLoading === 'complete'}
        title="Completar rodada"
        message="Existem partidas pendentes nesta rodada. Tem certeza que deseja completá-la? Partidas não jogadas não pontuarão."
        confirmLabel="Completar"
      />

      <ConfirmModal
        isOpen={!!showMarkPlayedConfirm}
        onClose={() => setShowMarkPlayedConfirm(null)}
        onConfirm={() => showMarkPlayedConfirm && handleMarkGroupPlayed(showMarkPlayedConfirm.groupId, showMarkPlayedConfirm.value)}
        isLoading={!!showMarkPlayedConfirm && actionLoading === `mark-played-${showMarkPlayedConfirm.groupId}`}
        title={showMarkPlayedConfirm?.value
          ? `Marcar Grupo ${showMarkPlayedConfirm.groupNumber} como jogado`
          : `Desfazer Grupo ${showMarkPlayedConfirm?.groupNumber ?? ''}`}
        message={showMarkPlayedConfirm?.value
          ? 'Os jogadores contam a rodada como jogada, mas não ganham nem perdem pontos. As partidas deste grupo não serão pontuadas.'
          : 'O grupo deixará de contar como jogado sem pontuação. As partidas voltarão a ficar pendentes.'}
        confirmLabel={showMarkPlayedConfirm?.value ? 'Marcar como jogado' : 'Desfazer'}
      />
    </AppShell>
  );
}

// ============================================================================
// Player Name (with W.O. marking)
// ============================================================================

function PlayerName({ player, woAbsentPlayerIds }: { player: RankingPlayer; woAbsentPlayerIds: string[] }) {
  const name = compactName(player.name) || '?';
  const isAbsent = woAbsentPlayerIds.includes(player.id);

  if (!isAbsent) return <>{name}</>;

  return (
    <span className="text-coral/70 line-through decoration-coral/50">{name}</span>
  );
}

// ============================================================================
// Match Card
// ============================================================================

function MatchCard({
  match,
  isAdmin,
  onSubmit,
  onDispute,
  onOverride,
  onWo,
  actionLoading,
}: {
  match: RankingMatchDetail;
  isAdmin: boolean;
  onSubmit: () => void;
  onDispute: () => void;
  onOverride: () => void;
  onWo: () => void;
  actionLoading: string | null;
}) {
  const statusConfig = {
    pending: { icon: null, className: '' },
    submitted: { icon: <CheckCircle className="w-3 h-3 text-green-400" />, className: '' },
    locked: { icon: <Lock className="w-3 h-3 text-white/30" />, className: 'opacity-60' },
  };

  const config = statusConfig[match.status];

  return (
    <div className={`p-3 rounded-xl bg-white/5 ${config.className}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-white/30 uppercase">Partida {match.matchNumber}</span>
        <div className="flex items-center gap-1.5">
          {match.isDisputed && <Flag className="w-3 h-3 text-coral" />}
          {match.isWo && match.woNoPenalty && (
            <span className="text-[10px] uppercase text-white/40">sem penalidade</span>
          )}
          {match.isWo && <Ban className={`w-3 h-3 ${match.woNoPenalty ? 'text-white/40' : 'text-sunny'}`} />}
          {config.icon}
        </div>
      </div>

      <div className="flex items-center justify-between text-sm gap-2">
        <div className="flex-1 min-w-0 leading-tight">
          <p className="text-white truncate">
            <PlayerName player={match.team1[0]} woAbsentPlayerIds={match.woAbsentPlayerIds} />
          </p>
          <p className="text-white truncate">
            <PlayerName player={match.team1[1]} woAbsentPlayerIds={match.woAbsentPlayerIds} />
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {match.team1Games !== null && match.team2Games !== null ? (
            <span className="font-bold text-white tabular-nums">
              {match.team1Games} x {match.team2Games}
              {match.tiebreakScore !== null && match.tiebreakScore !== undefined && (
                <span className="text-xs text-white/50 ml-0.5">({match.tiebreakScore})</span>
              )}
            </span>
          ) : (
            <span className="text-xs text-white/30">- x -</span>
          )}
        </div>
        <div className="flex-1 min-w-0 text-right leading-tight">
          <p className="text-white truncate">
            <PlayerName player={match.team2[0]} woAbsentPlayerIds={match.woAbsentPlayerIds} />
          </p>
          <p className="text-white truncate">
            <PlayerName player={match.team2[1]} woAbsentPlayerIds={match.woAbsentPlayerIds} />
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-1.5 mt-2">
        {match.canSubmit && (
          <button
            onClick={onSubmit}
            className="flex-1 text-xs py-1.5 px-2 rounded-lg bg-turquoise/10 text-turquoise hover:bg-turquoise/20 transition-colors"
          >
            Enviar resultado
          </button>
        )}
        {match.canDispute && (
          <button
            onClick={onDispute}
            disabled={actionLoading === `dispute-${match.id}`}
            className="flex items-center gap-1 text-xs py-1.5 px-2 rounded-lg bg-coral/10 text-coral hover:bg-coral/20 transition-colors disabled:opacity-50"
          >
            {actionLoading === `dispute-${match.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Flag className="w-3 h-3" />}
            Contestar
          </button>
        )}
        {isAdmin && match.status !== 'locked' && (
          <>
            <button
              onClick={onOverride}
              className="text-xs py-1.5 px-2 rounded-lg bg-white/5 text-white/50 hover:bg-white/10 transition-colors"
            >
              <Gavel className="w-3 h-3" />
            </button>
            {!match.isWo && (
              <button
                onClick={onWo}
                className="text-xs py-1.5 px-2 rounded-lg bg-white/5 text-white/50 hover:bg-white/10 transition-colors"
              >
                WO
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function RoundStatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string }> = {
    drafted: { label: 'Rascunho', className: 'bg-white/10 text-white/50' },
    in_progress: { label: 'Em andamento', className: 'bg-turquoise/20 text-turquoise' },
    completed: { label: 'Concluída', className: 'bg-green-500/20 text-green-400' },
  };
  const { label, className } = config[status] || config.drafted;
  return (
    <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      {label}
    </span>
  );
}

// ============================================================================
// Submit Match Modal
// ============================================================================

const VALID_SCORES = new Set([
  '7-6', '6-7', '7-5', '5-7', '6-4', '4-6', '6-3', '3-6',
  '6-2', '2-6', '6-1', '1-6', '6-0', '0-6',
]);

function SubmitMatchModal({
  match,
  onClose,
  onSubmit,
  isLoading,
  title = 'Enviar resultado',
}: {
  match: RankingMatchDetail | null;
  onClose: () => void;
  onSubmit: (matchId: string, team1Games: number, team2Games: number, tiebreakScore?: number | null) => void;
  isLoading: boolean;
  title?: string;
}) {
  const [team1Games, setTeam1Games] = useState('');
  const [team2Games, setTeam2Games] = useState('');
  const [tiebreakScore, setTiebreakScore] = useState('');

  // Reset when modal opens
  useEffect(() => {
    if (match) {
      setTeam1Games(match.team1Games?.toString() || '');
      setTeam2Games(match.team2Games?.toString() || '');
      setTiebreakScore(match.tiebreakScore?.toString() || '');
    }
  }, [match]);

  if (!match) return null;

  const getPlayerName = (p: RankingPlayer) => shortName(p.name) || '?';

  const t1 = parseInt(team1Games);
  const t2 = parseInt(team2Games);
  const isTiebreak = (t1 === 7 && t2 === 6) || (t1 === 6 && t2 === 7);
  const scoreKey = `${t1}-${t2}`;
  const hasValidScore = team1Games !== '' && team2Games !== '' && VALID_SCORES.has(scoreKey);
  const hasValidTiebreak = !isTiebreak || (tiebreakScore !== '' && parseInt(tiebreakScore) >= 0);
  const canSubmit = hasValidScore && hasValidTiebreak;

  const handleGameInput = (value: string, setter: (v: string) => void) => {
    const num = parseInt(value);
    if (value === '' || (num >= 0 && num <= 7)) {
      setter(value);
    }
  };

  // Determine validation message
  let validationMsg = '';
  if (team1Games !== '' && team2Games !== '') {
    if (t1 < 0 || t2 < 0) validationMsg = 'Placar não pode ser negativo';
    else if (t1 > 7 || t2 > 7) validationMsg = 'Placar não pode ser maior que 7';
    else if (!VALID_SCORES.has(scoreKey)) validationMsg = 'Resultados válidos: 6x0, 6x1, 6x2, 6x3, 6x4, 7x5, 7x6';
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-sm glass-card rounded-2xl p-6"
        >
          <div className="flex justify-between items-start mb-4">
            <h3 className="font-display text-lg font-bold text-white">{title}</h3>
            <button onClick={onClose} className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm text-white/60 mb-1">
                {getPlayerName(match.team1[0])} + {getPlayerName(match.team1[1])}
              </label>
              <input
                type="number"
                min="0"
                max="7"
                value={team1Games}
                onChange={(e) => handleGameInput(e.target.value, setTeam1Games)}
                placeholder="Games"
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-center text-lg font-bold focus:outline-none focus:border-turquoise/50"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">
                {getPlayerName(match.team2[0])} + {getPlayerName(match.team2[1])}
              </label>
              <input
                type="number"
                min="0"
                max="7"
                value={team2Games}
                onChange={(e) => handleGameInput(e.target.value, setTeam2Games)}
                placeholder="Games"
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-center text-lg font-bold focus:outline-none focus:border-turquoise/50"
              />
            </div>

            {isTiebreak && (
              <div>
                <label className="block text-sm text-white/60 mb-1">
                  Tie-break (pontos do perdedor)
                </label>
                <input
                  type="number"
                  min="0"
                  value={tiebreakScore}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || parseInt(v) >= 0) setTiebreakScore(v);
                  }}
                  placeholder="Ex: 3"
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-center text-lg font-bold focus:outline-none focus:border-turquoise/50"
                />
              </div>
            )}

            {validationMsg && (
              <p className="text-xs text-red-400 text-center">{validationMsg}</p>
            )}
          </div>

          <Button
            onClick={() => onSubmit(
              match.id,
              t1,
              t2,
              isTiebreak ? parseInt(tiebreakScore) : null,
            )}
            fullWidth
            isLoading={isLoading}
            disabled={!canSubmit}
          >
            Confirmar
          </Button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ============================================================================
// WO Modal
// ============================================================================

function WoModal({
  match,
  onClose,
  onSubmit,
  isLoading,
}: {
  match: RankingMatchDetail | null;
  onClose: () => void;
  onSubmit: (matchId: string, absentPlayerIds: string[], applyPenalty: boolean) => void;
  isLoading: boolean;
}) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [applyPenalty, setApplyPenalty] = useState(true);

  useEffect(() => {
    if (match) {
      setSelectedIds(new Set());
      setApplyPenalty(true);
    }
  }, [match]);

  if (!match) return null;

  const allPlayers = [...match.team1, ...match.team2];

  const togglePlayer = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-sm glass-card rounded-2xl p-6"
        >
          <div className="flex justify-between items-start mb-4">
            <h3 className="font-display text-lg font-bold text-white">Marcar W.O.</h3>
            <button onClick={onClose} className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className="text-sm text-white/50 mb-4">Selecione os jogadores ausentes:</p>

          <div className="space-y-2 mb-4">
            {allPlayers.map((player) => (
              <button
                key={player.id}
                onClick={() => togglePlayer(player.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                  selectedIds.has(player.id) ? 'bg-coral/20 border border-coral/40' : 'bg-white/5 border border-transparent hover:bg-white/10'
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-turquoise/30 to-coral/30 flex items-center justify-center text-xs font-bold text-white shrink-0">
                  {player.name?.charAt(0) || '?'}
                </div>
                <span className="text-sm text-white">{player.name || 'Jogador'}</span>
                {selectedIds.has(player.id) && <Ban className="w-4 h-4 text-coral ml-auto" />}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setApplyPenalty((v) => !v)}
            className="w-full flex items-start gap-3 p-3 mb-6 rounded-xl bg-white/5 border border-white/10 text-left hover:bg-white/10 transition-colors"
          >
            <div
              className={`mt-0.5 w-9 h-5 rounded-full shrink-0 transition-colors ${
                applyPenalty ? 'bg-coral' : 'bg-white/20'
              }`}
            >
              <div
                className={`w-4 h-4 mt-0.5 rounded-full bg-white transition-transform ${
                  applyPenalty ? 'translate-x-[18px]' : 'translate-x-0.5'
                }`}
              />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-white">Aplicar penalidade</p>
              <p className="text-xs text-white/50 mt-0.5">
                {applyPenalty
                  ? 'O ausente perde os pontos de W.O.'
                  : 'O ausente fica com 0 nesta partida. Use quando a falta não for culpa dele (lesão, trabalho).'}
              </p>
            </div>
          </button>

          <Button
            onClick={() => onSubmit(match.id, Array.from(selectedIds), applyPenalty)}
            fullWidth
            isLoading={isLoading}
            disabled={selectedIds.size === 0}
            className="!bg-coral !text-white hover:!bg-coral/80"
          >
            Confirmar W.O.
          </Button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ============================================================================
// Wildcard Modal
// ============================================================================

function WildcardModal({
  rankingId,
  roundId,
  groupId,
  slotPosition,
  allowCatchUp,
  onClose,
  onAssign,
  loadingPlayerId,
}: {
  rankingId: string;
  roundId: string;
  groupId: string;
  slotPosition: 2 | 3 | 4;
  allowCatchUp: boolean;
  onClose: () => void;
  onAssign: (groupId: string, slotPosition: 2 | 3 | 4, playerId: string) => void;
  loadingPlayerId: string | null;
}) {
  const [candidates, setCandidates] = useState<WildcardCandidate[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoadingCandidates(true);
      try {
        const response = await api.rankings.wildcardCandidates(rankingId, roundId, groupId);
        if (response.success && response.data) {
          setCandidates(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error('Failed to load candidates:', error);
      } finally {
        setLoadingCandidates(false);
      }
    };
    load();
  }, [rankingId, roundId, groupId]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm glass-card rounded-2xl overflow-hidden"
      >
        <div className="flex justify-between items-center p-4 border-b border-white/10">
          <h3 className="font-display text-lg font-bold text-white">Atribuir coringa</h3>
          <button onClick={onClose} className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!allowCatchUp && (
          <p className="px-4 py-3 text-[11px] text-white/50 leading-snug border-b border-white/10 bg-white/5">
            Coringas completam o grupo para as partidas acontecerem, mas não pontuam neste ranking.
          </p>
        )}

        <div className="max-h-80 overflow-y-auto">
          {loadingCandidates ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-white/30 animate-spin" />
            </div>
          ) : candidates.length === 0 ? (
            <p className="text-center text-white/40 text-sm py-8">Nenhum candidato disponível</p>
          ) : (
            <div className="divide-y divide-white/5">
              {candidates.map((candidate) => (
                <button
                  key={candidate.player.id}
                  onClick={() => onAssign(groupId, slotPosition, candidate.player.id)}
                  disabled={loadingPlayerId !== null}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors disabled:opacity-50"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-turquoise/30 to-coral/30 flex items-center justify-center text-xs font-bold text-white shrink-0">
                    {loadingPlayerId === candidate.player.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      candidate.player.name?.charAt(0) || '?'
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm text-white truncate">{candidate.player.name || 'Jogador'}</p>
                    <p className="text-[11px] text-white/40">
                      {candidate.roundsPlayed}/{candidate.totalCompletedRounds} rodadas
                    </p>
                  </div>
                  {allowCatchUp && (
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                      candidate.willEarnPoints
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-white/10 text-white/40'
                    }`}>
                      {candidate.willEarnPoints ? 'Ganha pts' : 'Sem pts'}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================================================
// Swap Player Modal
// ============================================================================

function SwapModal({
  rankingId,
  roundId,
  groupId,
  slotPosition,
  currentPlayerId,
  allowCatchUp,
  onClose,
  onSwap,
  loadingPlayerId,
}: {
  rankingId: string;
  roundId: string;
  groupId: string;
  slotPosition: 1 | 2 | 3 | 4;
  currentPlayerId: string;
  allowCatchUp: boolean;
  onClose: () => void;
  onSwap: (groupId: string, slotPosition: 1 | 2 | 3 | 4, newPlayerId: string) => void;
  loadingPlayerId: string | null;
}) {
  const [candidates, setCandidates] = useState<WildcardCandidate[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoadingCandidates(true);
      try {
        const response = await api.rankings.wildcardCandidates(rankingId, roundId, groupId);
        if (response.success && response.data) {
          const filtered = (Array.isArray(response.data) ? response.data : [])
            .filter((c) => c.player.id !== currentPlayerId);
          setCandidates(filtered);
        }
      } catch (error) {
        console.error('Failed to load candidates:', error);
      } finally {
        setLoadingCandidates(false);
      }
    };
    load();
  }, [rankingId, roundId, currentPlayerId]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm glass-card rounded-2xl overflow-hidden"
      >
        <div className="flex justify-between items-center p-4 border-b border-white/10">
          <h3 className="font-display text-lg font-bold text-white">Trocar jogador</h3>
          <button onClick={onClose} className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="max-h-80 overflow-y-auto">
          {loadingCandidates ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-white/30 animate-spin" />
            </div>
          ) : candidates.length === 0 ? (
            <p className="text-center text-white/40 text-sm py-8">Nenhum candidato disponível</p>
          ) : (
            <div className="divide-y divide-white/5">
              {candidates.map((candidate) => (
                <button
                  key={candidate.player.id}
                  onClick={() => onSwap(groupId, slotPosition, candidate.player.id)}
                  disabled={loadingPlayerId !== null}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors disabled:opacity-50"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-turquoise/30 to-coral/30 flex items-center justify-center text-xs font-bold text-white shrink-0">
                    {loadingPlayerId === candidate.player.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      candidate.player.name?.charAt(0) || '?'
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm text-white truncate">{candidate.player.name || 'Jogador'}</p>
                    <p className="text-[11px] text-white/40">
                      {candidate.roundsPlayed}/{candidate.totalCompletedRounds} rodadas
                    </p>
                  </div>
                  {allowCatchUp && (
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                      candidate.willEarnPoints
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-white/10 text-white/40'
                    }`}>
                      {candidate.willEarnPoints ? 'Ganha pts' : 'Sem pts'}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================================================
// Confirm Modal
// ============================================================================

function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  title,
  message,
  confirmLabel,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  title: string;
  message: string;
  confirmLabel: string;
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm glass-card rounded-2xl p-6"
          >
            <h3 className="font-display text-lg font-bold text-white mb-2">{title}</h3>
            <p className="text-white/60 text-sm mb-6">{message}</p>
            <div className="flex gap-3">
              <Button onClick={onClose} variant="secondary" fullWidth>Cancelar</Button>
              <Button onClick={onConfirm} isLoading={isLoading} fullWidth className="!bg-coral !text-white hover:!bg-coral/80">
                {confirmLabel}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
